import { getMobileBookingProgress } from '@components/BookingFlow/Common/bookingFlowProgress';
import { BookingScreen } from '@components/BookingFlow/Mobile/BookingScreen';
import { CarImage } from '@components/Common/CarImage';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { CarSpec } from 'noddi-types';
import { NoddiCard, NoddiCheckbox, NoddiDatePicker, NoddiSelect } from 'noddi-ui-web';
import { compareLicensePlates, customerRoutes, insuranceCompanies } from 'noddi-util';
import { useBookingActions, useBookingCars, useBookingCarsInsuranceInfo, useIsInsuranceStepValid } from 'noddi-web';
import { useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCarName } from '../../../../utils/carName';
import { getCommonTranslations } from '../../../../utils/commonTranslations';

export const InsuranceInfo = () => {
  const navigate = useNavigate();

  const isInsuranceStepValid = useIsInsuranceStepValid();

  const selectedCars = useBookingCars();

  return (
    <BookingScreen
      progress={getMobileBookingProgress('summary')}
      // oxlint-disable-next-line typescript/no-misused-promises -- async onNextClicked runs navigate(); React discards the returned promise, so a rejection is unhandled unless the body catches
      onNextClicked={() => navigate(customerRoutes.newBookingSummary.getPath())}
      disableNextButton={!isInsuranceStepValid}
      validationVariant='insuranceInfoRequired'
      title={t`Insurance company and date of damage`}
      hideShoppingCart
    >
      <div className='flex flex-col gap-4'>
        {selectedCars.map((car) => (
          <InsuranceInfoForCar key={car.licensePlate.number} car={car} />
        ))}
      </div>
    </BookingScreen>
  );
};

const InsuranceInfoForCar = ({ car }: { car: CarSpec }) => {
  const insuranceInfo = useBookingCarsInsuranceInfo();
  const { setInsuranceCompanyForCar, setDateOfDamageForCar, setIsGlassDamageCoveredForCar } = useBookingActions();

  const requiredInsuranceInfo = insuranceInfo?.find((insurance) =>
    compareLicensePlates(insurance.licensePlate, car.licensePlate)
  );

  const carNameId = useId();

  return (
    // A group named by the car, not a fieldset: a legend cannot hold the car image.
    <div role='group' aria-labelledby={carNameId} className='flex flex-col gap-4'>
      <div className='flex items-center gap-4'>
        <CarImage car={car} alt='' size='thumbnail' />
        <span id={carNameId} className='font-semibold'>
          {getCarName({ car, showLicensePlate: true })}
        </span>
      </div>

      <div className='flex flex-col gap-4'>
        <NoddiCard contentClassName='flex flex-col gap-2'>
          <NoddiSelect
            label={t`Insurance company`}
            disabled={requiredInsuranceInfo?.isGlassDamageCovered === false}
            value={requiredInsuranceInfo?.insuranceCompanyName}
            options={insuranceCompanies}
            onChange={(value) =>
              setInsuranceCompanyForCar({ licensePlate: car.licensePlate, insuranceCompanyName: value })
            }
            translation={{ ...getCommonTranslations().select, placeholder: t`Select insurance company` }}
          />
          <NoddiCheckbox
            label={t`My insurance does not cover glass damage`}
            defaultChecked={requiredInsuranceInfo?.isGlassDamageCovered === false}
            onCheckedChange={(checked) =>
              setIsGlassDamageCoveredForCar({ licensePlate: car.licensePlate, isGlassDamageCovered: !checked })
            }
          />
          {requiredInsuranceInfo?.isGlassDamageCovered === false && (
            <p className='text-sm text-muted-foreground'>
              <Trans>
                If your car only has liability insurance, you will have to pay yourself. You’ll receive the exact price
                in the order confirmation after you’ve booked an appointment.
              </Trans>
            </p>
          )}
        </NoddiCard>
        <NoddiCard contentClassName='flex flex-col gap-2'>
          <p>
            <Trans>
              Choose the date of damage so we can report it to your insurance company without needing to call you
            </Trans>
          </p>
          <p className='text-sm text-muted-foreground'>
            <Trans>It’s enough that you give us your best estimate.</Trans>
          </p>
          <NoddiDatePicker
            label={t`Date of damage`}
            disabled={{ after: new Date() }}
            date={requiredInsuranceInfo?.dateOfDamage ? new Date(requiredInsuranceInfo.dateOfDamage) : undefined}
            onDateChange={(date) => setDateOfDamageForCar({ licensePlate: car.licensePlate, dateOfDamage: date })}
            translation={{ pickADate: t`Choose date of damage` }}
          />
        </NoddiCard>
      </div>
    </div>
  );
};
